import React, { Component, Fragment } from 'react';
import classNames from 'classnames';

import { Input, Button, Col, Row, Form, Popover, Spin, Tag } from 'antd';
import { Close, MathPlus } from '@nackle/origami-icons';
import { isValidColor } from '../../utils/color';
import { getName } from '../../utils/hierarchy';

import ColorDisplay from '../color-display/color-display';

import ColorStore from '../../stores/color';

import './color-palette.less';

export default class ColorPalette extends Component {
    state = {
        loading: false,
        colors: [],
        newColorMenuVisible: false,
        newColor: '',
        newColorValid: false
    };

    static getDerivedStateFromProps( props, state ) {
        if (
            state.colorStore &&
            state.colorStore.key !== new ColorStore( props ).key
        ) {
            return {
                colorStore: null
            };
        }
        return null;
    }

    componentDidMount() {
        this.load();
    }
    componentWillUnmount() {
        const { colorStore } = this.state;

        if ( colorStore ) {
            colorStore.colorsChange.off( this.handleColorsChange );
        }
    }
    componentDidUpdate( prevProps, prevState ) {
        const { loading, colorStore } = this.state;

        if ( !colorStore && !loading ) {
            this.load();
        }
    }

    render() {
        const { readOnly, compact } = this.props;
        const { loading, colors, requestedPaletteType, actualPaletteType } = this.state;

        return (
            <div className={ classNames( 'branding-color-palette', { compact } ) }>
                {
                    loading ? (
                        <Spin />
                    ) : (
                        <Form layout="vertical">
                            <Form.Item
                                className="palette-property-item"
                                label={ (
                                    <Fragment>
                                        <span>{ this.renderLabel() }</span>
                                        {
                                            requestedPaletteType !== actualPaletteType && (
                                                <div className="fallback-tag-container">
                                                    <Tag
                                                        color="blue"
                                                        className="fallback-tag"
                                                    >
                                                        { getName( actualPaletteType ) }
                                                    </Tag>
                                                </div>
                                            )
                                        }
                                    </Fragment>
                                ) }
                            >
                                <ul className="color-list">
                                    {
                                        colors.filter( c => !!c.hex ).map( color => (
                                            <li key={ color.hex }>
                                                <ColorDisplay
                                                    value={ color.hex }
                                                    text={ color.hex }
                                                />
                                                {
                                                    !readOnly && (
                                                        <a
                                                            className="delete"
                                                            href="delete"
                                                            onClick={ ( e ) => {
                                                                e.preventDefault();
                                                                this.handleDeleteColorClick( color );
                                                            } }
                                                        >
                                                            <Close  />
                                                        </a>
                                                    )
                                                }
                                            </li>
                                        ) )
                                    }
                                    { !readOnly && this.renderNewColorButton() }
                                </ul>
                            </Form.Item>
                        </Form>
                    )
                }
            </div>
        );
    }
    renderNewColorButton = () => {
        const {
            newColor,
            newColorValid,
            newColorMenuVisible
        } = this.state;

        return (
            <li>
                <Popover
                    trigger="click"
                    placement="bottom"
                    visible={ newColorMenuVisible }
                    onVisibleChange={
                        newColorMenuVisible => {
                            this.setState( {
                                newColorMenuVisible
                            }, () => {
                                if ( newColorMenuVisible ) {
                                    setTimeout( () => {
                                        document.getElementById( 'newColorInput' ).focus();
                                    }, 333 );
                                }
                            } );
                        }
                    }
                    title={ (
                        <div className="branding-color-picker-input">
                            <Input
                                id="newColorInput"
                                autoFocus
                                value={ newColor }
                                onChange={ this.handleNewColorChange }
                                onKeyUp={ this.handleNewColorKeyUp }
                                autoComplete="off"
                            />
                            <ColorDisplay value={ newColor } />
                        </div>
                    ) }
                    content={ (
                        <div className="branding-color-picker-menu">
                            <Row>
                                <Col>
                                    <Button
                                        type="default"
                                        onClick={ this.handleNewColorCancelClick }
                                    >
                                        <span>Cancel</span>
                                    </Button>
                                </Col>
                                <Col flex={ 1 } />
                                <Col>
                                    <Button
                                        type="primary"
                                        onClick={ this.handleNewColorAddClick }
                                        disabled={ !newColorValid }
                                    >
                                        <span>Add Color</span>
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    ) }
                >
                    <Button
                        className="add"
                        type="primary"
                        ghost={ true }
                    >
                        <MathPlus />
                    </Button>
                </Popover>
            </li>
        );
    };
    renderLabel = () => {
        const { onRenderLabel } = this.props;
        const label = 'Colors';

        if ( typeof onRenderLabel === 'function' ) {
            return onRenderLabel( { label } );
        }

        return label;
    };

    handleDeleteColorClick = ( color ) => {
        const { colorStore } = this.state;
        colorStore.removeColor( color.id );
    };
    handleNewColorCancelClick = () => {
        this.setState( {
            newColorMenuVisible: false,
            newColor: '',
            newColorValid: false
        } );
    };
    handleNewColorAddClick = async () => {
        const { colorStore } = this.state;
        colorStore.addColor( this.state.newColor );

        this.setState( {
            newColorMenuVisible: false,
            newColor: '',
            newColorValid: false
        } );
    };
    handleNewColorChange = ( e ) => {
        this.setState( {
            newColor: e.target.value,
            newColorValid: isValidColor( e.target.value )
        } );
    };
    handleNewColorKeyUp = ( e ) => {
        if ( e.keyCode === 13 ) {
            this.handleNewColorAddClick();
        }
    };

    handleColorsChange = ( { colors, requestedPaletteType, actualPaletteType } ) => {
        this.setState( { colors, requestedPaletteType, actualPaletteType } );
    };

    load = async () => {
        let { colorStore } = this.state;

        if ( colorStore ) {
            colorStore.colorsChange.off( this.handleColorsChange );
        }

        colorStore = new ColorStore( this.props );
        colorStore.colorsChange.on( this.handleColorsChange );

        this.setState( {
            loading: true,
            colorStore
        } );

        try {
            const {
                colors,
                requestedPaletteType,
                actualPaletteType
            } = await colorStore.getColors();

            this.setState( {
                loading: false,
                colors,
                requestedPaletteType,
                actualPaletteType
            } );
        } catch ( { errors } ) {
            this.setState( {
                loading: false,
                loadingErrors: errors
            } );
        }
    };
    save = async () => {
        const { colorStore } = this.state;

        this.setState( {
            saving: true
        } );

        try {
            const { colors } = await colorStore.saveColors();

            this.setState( {
                saving: false,
                colors
            } );
        } catch ( ex ) {
            this.setState( {
                saving: false
            } );
            throw ex;
        }
    };
}
